<template>
    <div class="stock-movements-page">
        <div class="smp-header">
            <h2 class="smp-title ppv2-text-black">{{ $t('stock.movements.title') }}</h2>
            <div class="smp-actions">
                <StockPeriodPicker :value="period" @input="setPeriod" />
                <BaseButton
                    variant="navyOutline"
                    :custom-height="40"
                    :custom-font-size="14"
                    custom-class="ppv2-gap-2 !ppv2-px-4 smp-mockup-btn"
                    :loading="exporting"
                    :disabled="exporting"
                    @click="exportCsv"
                >
                    <Download v-if="!exporting" :size="16" />
                    <span class="poppinsMedium">{{ $t('stock.movements.export') }}</span>
                </BaseButton>
            </div>
        </div>

        <StockMovementsTable
            :groups="groups"
            :sortBy="sortBy"
            :order="order"
            :loading="loading"
            :hasMore="hasMore"
            :canEditNotes="canEditNotes"
            @sort="setSort"
            @load-more="loadMore"
            @save-note="saveNote"
        />
    </div>
</template>

<script>
import { Download } from 'lucide-vue'
import BaseButton from '../../Shared/BaseButton.vue'
import StockPeriodPicker from './StockPeriodPicker.vue'
import StockMovementsTable from './StockMovementsTable.vue'
import downloadBlob from '../../../utils/downloadBlob'
import { formatPeriodLabel, periodStorageKey, rememberPeriod, restorePeriod } from './stockUtils'

// 30 lignes par page ; la suite se charge au défilement (sentinelle de StockMovementsTable).
const PER_PAGE = 30
const TOAST_OPTIONS = { timeout: 5000 }

export default {
    name: 'StockMovementsPage',
    components: { Download, BaseButton, StockPeriodPicker, StockMovementsTable },
    props: {
        /** Ex. `/back-office/v3/food/stock-movements` : `/list`, `/export`, `/{uuid}` en découlent. */
        apiBase: {
            type: String,
            required: true,
        },
        /** Permission serveur (« Modifier un produit ») ; faux ⇒ notes en lecture seule. */
        canEditNotes: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            // Dernier choix de l'utilisateur (navigateur), sinon « 7 derniers jours ».
            period: restorePeriod(periodStorageKey(this.apiBase)),
            sortBy: 'time',
            order: 'asc',
            rows: [],
            days: [],
            page: 1,
            lastPage: 1,
            timezone: null,
            loading: false,
            exporting: false,
            requestSeq: 0,
        }
    },
    computed: {
        locale() {
            return (this.$i18n && this.$i18n.locale) || 'fr'
        },
        hasMore() {
            return this.page < this.lastPage
        },
        dayCounts() {
            const counts = {}
            ;(this.days || []).forEach((day) => {
                if (day && day.date) counts[day.date] = Number(day.count) || 0
            })
            return counts
        },
        groups() {
            const byDate = new Map()
            this.rows.forEach((row) => {
                const date = row.date || (row.occurred_at ? String(row.occurred_at).slice(0, 10) : '')
                if (!byDate.has(date)) {
                    byDate.set(date, {
                        date,
                        label: formatPeriodLabel(date, date, { locale: this.locale }),
                        rows: [],
                    })
                }
                byDate.get(date).rows.push(row)
            })
            return Array.from(byDate.values()).map((group) => ({
                ...group,
                count: this.dayCounts[group.date] !== undefined ? this.dayCounts[group.date] : group.rows.length,
            }))
        },
        queryParams() {
            return {
                from: this.period.from,
                to: this.period.to,
                sort: this.sortBy,
                order: this.order,
            }
        },
    },
    methods: {
        async fetchMovements({ append = false } = {}) {
            const seq = ++this.requestSeq
            this.loading = true
            try {
                const response = await axios.get(`${this.apiBase}/list`, {
                    params: { ...this.queryParams, page: this.page, per_page: PER_PAGE },
                })
                if (seq !== this.requestSeq) return // une requête plus récente a pris le relais
                const data = Array.isArray(response.data.data) ? response.data.data : []
                const meta = response.data.meta || {}
                this.rows = append ? this.rows.concat(data) : data
                this.days = Array.isArray(meta.days) ? meta.days : []
                this.lastPage = Number(meta.last_page) || 1
                this.page = Number(meta.current_page) || this.page
                this.timezone = meta.timezone || null
            } catch (error) {
                if (seq !== this.requestSeq) return
                if (!append) this.rows = []
                this.$toast.error(this.errorMessage(error, 'stock.movements.load_error'), TOAST_OPTIONS)
            } finally {
                if (seq === this.requestSeq) this.loading = false
            }
        },
        reload() {
            this.page = 1
            this.lastPage = 1
            return this.fetchMovements()
        },
        setPeriod(range) {
            if (!range || !range.from) return
            this.period = { from: range.from, to: range.to || range.from }
            rememberPeriod(periodStorageKey(this.apiBase), this.period)
            this.reload()
        },
        setSort(key) {
            if (this.sortBy === key) {
                this.order = this.order === 'asc' ? 'desc' : 'asc'
            } else {
                this.sortBy = key
                this.order = 'asc'
            }
            this.reload()
        },
        loadMore() {
            if (!this.hasMore || this.loading) return
            this.page += 1
            this.fetchMovements({ append: true })
        },
        async saveNote({ uuid, note }) {
            const row = this.rows.find((r) => r.uuid === uuid)
            if (!row) return
            const previous = row.note
            row.note = note || null // optimiste
            try {
                const response = await axios.patch(`${this.apiBase}/${uuid}`, { note: note || null })
                const saved = response && response.data && response.data.data
                if (saved && Object.prototype.hasOwnProperty.call(saved, 'note')) {
                    row.note = saved.note
                }
            } catch (error) {
                row.note = previous
                this.$toast.error(this.errorMessage(error, 'stock.movements.note_error'), TOAST_OPTIONS)
            }
        },
        async exportCsv() {
            if (this.exporting) return
            this.exporting = true
            try {
                const response = await axios.get(`${this.apiBase}/export`, {
                    params: this.queryParams,
                    responseType: 'blob',
                })
                downloadBlob(
                    response.data,
                    `mouvements-stock-${this.period.from}_${this.period.to}.csv`,
                    'text/csv;charset=utf-8'
                )
            } catch (error) {
                this.$toast.error(this.errorMessage(error, 'stock.movements.export_error'), TOAST_OPTIONS)
            } finally {
                this.exporting = false
            }
        },
        errorMessage(error, fallbackKey) {
            const data = error && error.response && error.response.data
            if (data && typeof data.message === 'string' && data.message && !(data instanceof Blob)) {
                return data.message
            }
            return this.$t(fallbackKey)
        },
    },
    mounted() {
        this.reload()
    },
}
</script>

<style scoped lang="scss">
// Boutons « période » / « Exporter » de la maquette : pilule blanche, bordure navy, survol gris #eef1f5.
// Surcharge locale (racine de BaseButton = élément scopé ici) : la variante navyOutline reste inchangée.
.smp-mockup-btn {
    background: #fff !important;
    border: 1px solid #021832 !important;
    border-radius: 999px !important;
    color: #021832 !important;
    padding: 0 20px !important;
    font-size: 13px !important;
    box-shadow: none !important;

    &:hover:not(:disabled) {
        background: #eef1f5 !important;
        border-color: #021832 !important;
        color: #021832 !important;
    }
}

.dark .smp-mockup-btn {
    background: transparent !important;
    border-color: rgba(255, 255, 255, 0.6) !important;
    color: #fff !important;
}

.smp-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
    margin-bottom: 20px;
}

.smp-title {
    margin: 0;
    font-family: 'Poppins', sans-serif;
    font-size: 25px;
    font-weight: 400;
    letter-spacing: -0.5px;
    color: #021832;
}

.dark .smp-title {
    color: #fff;
}

.smp-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
}
</style>
